import React, { useState } from 'react';
import { isLikelyBlockedLinkedInCdnImageUrl, reportBrokenPhoto } from '@/utils/photoUtils';

interface PersonAvatarProps {
  personId: string;
  hostedPhotoUrl?: string | null;
  linkedinPhotoUrl?: string | null;
  firstName?: string;
  lastName?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
};

export default function PersonAvatar({
  personId,
  hostedPhotoUrl,
  linkedinPhotoUrl,
  firstName,
  lastName,
  name,
  size = 'md',
  className = '',
}: PersonAvatarProps) {
  const [imgError, setImgError] = useState(false);

  // Prefer hosted; otherwise LinkedIn CDN URLs are skipped (hotlink 403/404 → console noise).
  const rawPhotoUrl = (hostedPhotoUrl || linkedinPhotoUrl)?.trim() || null;
  const photoUrl =
    rawPhotoUrl && !isLikelyBlockedLinkedInCdnImageUrl(rawPhotoUrl) ? rawPhotoUrl : null;

  // Generate initials
  const getInitials = () => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (name) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    return '??';
  };

  const handleError = () => {
    if (!imgError) {
      setImgError(true);
      // Report broken photo for re-sync
      if (personId) {
        reportBrokenPhoto(personId);
      }
    }
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;

  // Show initials if no photo or error
  if (!photoUrl || imgError) {
    return (
      <div
        className={`${sizeClass} rounded-full bg-gray-200 flex items-center justify-center font-bold text-[#5C6B7A] flex-shrink-0 ${className}`}
      >
        {getInitials()}
      </div>
    );
  }

  return (
    <div className={`relative ${sizeClass} flex-shrink-0 ${className}`}>
      {/* Initials fallback behind image */}
      <div
        className={`absolute inset-0 rounded-full bg-gray-200 flex items-center justify-center font-bold text-[#5C6B7A] -z-10`}
      >
        {getInitials()}
      </div>
      {/* Actual image */}
      <img
        src={photoUrl}
        alt={name || `${firstName} ${lastName}` || 'Profile'}
        className={`${sizeClass} rounded-full object-cover`}
        onError={handleError}
        referrerPolicy="no-referrer"
      />
    </div>
  );
}

